import React, { useEffect, useState } from "react";
import classes from "./contests.module.css";
import Sidebar from "./Sidebar";
import ArtistCard from "./ArtistCard";
import PerformanceModal from "./PerformanceModal";
import Pagination from "./Pagination";
import LoadingSpinner from "./LoadingSpinner";
import { useNavigate, useParams } from "react-router-dom";
import {
  castVote,
  fetchActiveContests,
  fetchParticipantsByCategory,
} from "../../redux/slices/andArenaSlice";
import { useDispatch, useSelector } from "react-redux";

const Contests = () => {
  const [activeCategory, setActiveCategory] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8); // Fixed number of items per page

  // Get state from Redux
  const { andArenaArtistsByCategory, totalAndArenaArtistsByCategory, loading } =
    useSelector((state) => state.andArena);

  const activeContests = useSelector((state) => state.andArena.activeContests);

  const router = useNavigate();
  const { contestCategory } = useParams();
  const dispatch = useDispatch();

  const categoryToCategoryMap = (categoryFromFrontEnd) => {
    switch (categoryFromFrontEnd) {
      case "singing":
        return "singer";
      case "video-editing":
        return "video_editor";
      case "photography":
        return "photography";
      case "magic":
        return "magician";
      case "dancing":
        return "dancer";
      default:
        return "singer";
    }
  };

  const categories = [
    { id: "singing", name: "Singing", categoryBackendId: "singer" },
    {
      id: "photography",
      name: "Photography",
      categoryBackendId: "photography",
    },
    { id: "dancing", name: "Dancing", categoryBackendId: "dancer" },
    {
      id: "video-editing",
      name: "Video Editing",
      categoryBackendId: "video_editor",
    },
    { id: "magic", name: "Magic", categoryBackendId: "magician" },
  ];

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setCurrentPage(1); // Reset to first page when changing category
    router(`/artist-next-door-arena/contests/${categoryId}`);

    // Clear existing data first to prevent showing old data
    // This should be handled in your Redux slice to clear andArenaArtistsByCategory

    // Fetch artists for the new category
    dispatch(
      fetchParticipantsByCategory({
        category: categoryToCategoryMap(categoryId),
        page_number: 1,
        page_size: 100, // Fetch a larger batch or all artists
      })
    );
  };

  const handleVote = async (artist) => {
    await dispatch(
      castVote({
        username: artist?.participant?.username,
        contest_id: artist?.contestInfo?.contest_id,
      })
    );

    // Refresh all artists with updated vote counts
    dispatch(
      fetchParticipantsByCategory({
        category: categoryToCategoryMap(activeCategory),
        page_number: 1,
        page_size: 100, // Fetch a larger batch or all artists
      })
    );
  };

  const openPerformanceModal = (artist) => {
    setSelectedArtist(artist);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedArtist(null);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);

    // Scroll to top when changing pages
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    setActiveCategory(contestCategory);
  }, [contestCategory]);

  console.log(
    "totalAndArenaArtistsByCategory are.",
    totalAndArenaArtistsByCategory
  );

  useEffect(() => {
    if (activeCategory) {
      // Initial data fetch - get all artists for the category
      dispatch(
        fetchParticipantsByCategory({
          category: categoryToCategoryMap(activeCategory),
          page_number: 1,
          page_size: 100, // Fetch a larger batch or all artists
        })
      );
      dispatch(fetchActiveContests({ page_number: 1, page_size: 10 }));
    }
  }, [activeCategory, dispatch]);

  return (
    <div className={classes.votingArena}>
      <Sidebar
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={handleCategoryChange}
      />

      <main className={classes.mainContent}>
        <header className={classes.pageHeader}>
          <h1>Artist Voting Arena</h1>
          <p>
            Vote for your favorite artists in the{" "}
            {categories?.find((cat) => cat.id === activeCategory)?.name || ""}{" "}
            category
          </p>
        </header>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className={classes.artistsGrid}>
              {andArenaArtistsByCategory?.length > 0 ? (
                // Get paginated artists client-side
                andArenaArtistsByCategory
                  .slice(
                    (currentPage - 1) * itemsPerPage,
                    currentPage * itemsPerPage
                  )
                  .map((artist) => (
                    <ArtistCard
                      key={artist?.id}
                      artist={artist}
                      onVote={() => handleVote(artist)}
                      onViewPerformance={() => openPerformanceModal(artist)}
                    />
                  ))
              ) : (
                <div className={classes.noArtistsMessage}>
                  No artists found in this category
                </div>
              )}
            </div>

            {andArenaArtistsByCategory?.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={
                  Math.ceil(totalAndArenaArtistsByCategory / itemsPerPage) || 1
                }
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </main>

      {isModalOpen && selectedArtist && (
        <PerformanceModal
          artist={selectedArtist}
          onClose={closeModal}
          activeCategory={activeCategory}
          onVoteSuccess={() => {
            // Refresh artists after successful vote from modal
            dispatch(
              fetchParticipantsByCategory({
                category: categoryToCategoryMap(activeCategory),
                page_number: 1,
                page_size: 100,
              })
            );
          }}
        />
      )}
    </div>
  );
};

export default Contests;
